import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

/* ─── Animated compass needle SVG ─── */
function HeroCompass() {
  return (
    <div style={{ position: 'relative', width: '320px', height: '320px', margin: '0 auto' }}>
      {/* Outer rings */}
      {[1, 0.7, 0.45].map((scale, i) => (
        <motion.div key={i}
          style={{
            position: 'absolute', inset: 0,
            border: `1px solid rgba(168,207,224,${0.08 + i * 0.04})`,
            borderRadius: '50%',
            transform: `scale(${scale})`,
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 40 - i * 10, repeat: Infinity, ease: 'linear' }}
        />
      ))}

      {/* Degree marks */}
      <svg
        viewBox="0 0 320 320"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        {Array.from({ length: 36 }, (_, i) => {
          const angle = (i * 10 * Math.PI) / 180;
          const r1 = 148, r2 = i % 9 === 0 ? 132 : 140;
          const cx = 160, cy = 160;
          return (
            <line key={i}
              x1={cx + r1 * Math.sin(angle)} y1={cy - r1 * Math.cos(angle)}
              x2={cx + r2 * Math.sin(angle)} y2={cy - r2 * Math.cos(angle)}
              stroke={i % 9 === 0 ? 'rgba(201,168,76,0.5)' : 'rgba(168,207,224,0.15)'}
              strokeWidth={i % 9 === 0 ? 1.5 : 0.8}
            />
          );
        })}
        {/* Cardinal labels */}
        {[
          { label: 'N', x: 160, y: 22, color: '#c9a84c' },
          { label: 'E', x: 302, y: 165, color: 'rgba(168,207,224,0.6)' },
          { label: 'S', x: 160, y: 306, color: 'rgba(168,207,224,0.6)' },
          { label: 'W', x: 18, y: 165, color: 'rgba(168,207,224,0.6)' },
        ].map(({ label, x, y, color }) => (
          <text key={label} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontFamily="Syne, sans-serif" fontWeight="700" fill={color}>
            {label}
          </text>
        ))}
      </svg>

      {/* Rotating needle */}
      <motion.div
        style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
        animate={{ rotate: [0, 23, 23, -8, -8, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <svg viewBox="0 0 60 120" width="34" height="68">
          <polygon points="30,0 36,60 30,72 24,60" fill="#c9a84c" />
          <polygon points="30,72 36,60 30,120 24,60" fill="rgba(168,207,224,0.4)" />
          <circle cx="30" cy="60" r="5" fill="#c9a84c" />
          <circle cx="30" cy="60" r="2.5" fill="#0d1f35" />
        </svg>
      </motion.div>

      {/* Center glow */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(201,168,76,0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

/* ─── Activity card ─── */
function ActivityCard({ to, icon, title, category, description, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link to={to} style={{ textDecoration: 'none' }}>
        <div className="card" style={{ height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
          {/* Top accent line */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)', opacity: 0.4 }} />

          <div style={{ fontSize: '2.4rem', marginBottom: '14px' }}>{icon}</div>
          <div className="label label-amber" style={{ marginBottom: '12px' }}>{category}</div>
          <h3 style={{ color: '#fff', marginBottom: '10px', fontSize: '1.3rem' }}>{title}</h3>
          <p style={{ fontSize: '0.88rem', lineHeight: 1.7 }}>{description}</p>

          <div style={{
            marginTop: '20px', display: 'flex', alignItems: 'center', gap: '6px',
            fontFamily: 'Syne, sans-serif', fontSize: '0.82rem', fontWeight: 600,
            color: '#c9a84c',
          }}>
            Begin activity
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8H13M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

/* ─── Scoring step ─── */
function ScoringStep({ number, title, desc }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: number * 0.1, duration: 0.5 }}
      style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}
    >
      <div style={{
        width: '40px', height: '40px', flexShrink: 0,
        border: '1.5px solid rgba(201,168,76,0.4)',
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Syne, sans-serif', fontWeight: 800,
        fontSize: '0.85rem', color: '#c9a84c',
        background: 'rgba(201,168,76,0.05)',
      }}>
        {String(number).padStart(2, '0')}
      </div>
      <div>
        <h4 style={{ color: '#fff', marginBottom: '4px' }}>{title}</h4>
        <p style={{ fontSize: '0.88rem', lineHeight: 1.65 }}>{desc}</p>
      </div>
    </motion.div>
  );
}

/* ─── HOME PAGE ─── */
export default function Home() {
  return (
    <>
      {/* ── HERO ── */}
      <section style={{
        position: 'relative', minHeight: '90vh',
        display: 'flex', alignItems: 'center',
        overflow: 'hidden',
        padding: '80px 0 60px',
      }}>
        {/* Grid background */}
        <div className="grid-bg" />

        {/* Subtle radial glow */}
        <div style={{
          position: 'absolute', top: '-20%', right: '-10%',
          width: '60vw', height: '60vw', maxWidth: '700px', maxHeight: '700px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(74,127,165,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '60px',
            alignItems: 'center',
          }} className="hero-grid">

            {/* Text */}
            <div style={{ maxWidth: '560px' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
              >
                <div className="label label-amber" style={{ marginBottom: '20px' }}>
                  Canada · Adults 18+ · Free Activities
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                style={{ color: '#fff', marginBottom: '24px' }}
              >
                <span>Find Your</span>
                <br />
                <span
                  style={{
                    background: 'linear-gradient(135deg, #c9a84c 0%, #e8c46a 50%, #c9a84c 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  North
                </span>
              </motion.h1>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  color: 'rgba(168,207,224,0.85)',
                  fontSize: 'clamp(1.1rem, 2.6vw, 1.6rem)',
                  marginBottom: '24px',
                }}
              >
                Navigation activities for Canadian adults
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.7 }}
                style={{ maxWidth: '440px', marginBottom: '36px', fontSize: '1.05rem' }}
              >
                Browser-based navigation activities built for Canadian adults. Chart your course, read the compass, find the route — all with session points that carry no monetary value.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}
              >
                <Link to="/activities" className="btn btn-primary">Explore Activities</Link>
                <Link to="/how-it-works" className="btn btn-outline">How It Works</Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                style={{ marginTop: '28px', display: 'flex', gap: '28px', flexWrap: 'wrap' }}
              >
                {[
                  { val: 'Free', label: 'Always free' },
                  { val: '18+', label: 'Adults only' },
                  { val: '0', label: 'Real money' },
                ].map(({ val, label }) => (
                  <div key={label}>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#c9a84c' }}>{val}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(168,207,224,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Compass */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <HeroCompass />
            </motion.div>
          </div>
        </div>
        <style>{`@media(min-width:900px){.hero-grid{grid-template-columns:1fr 1fr !important;}}`}</style>
      </section>

      {/* ── ACTIVITIES ── */}
      <section className="section" style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '1px', height: '100%', background: 'linear-gradient(transparent, rgba(201,168,76,0.1), transparent)', pointerEvents: 'none' }} />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginBottom: '48px' }}
          >
            <div className="label label-sky" style={{ marginBottom: '14px' }}>Three Activities</div>
            <h2 style={{ color: '#fff', marginBottom: '16px', maxWidth: '500px' }}>
              Chart Your Course
            </h2>
            <div className="divider" />
            <p style={{ maxWidth: '480px', marginTop: '14px' }}>
              Three original navigation-themed activities, each with a distinct interaction model. No shared mechanics, no reused patterns.
            </p>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            <ActivityCard
              to="/activities/compass-read"
              icon="🧭"
              title="Compass Read"
              category="Timing · Precision"
              description="A compass needle sweeps across cardinal points. Tap to stop it precisely in your target sector. The narrower the sector, the more session points awarded."
              delay={0}
            />
            <ActivityCard
              to="/activities/route-builder"
              icon="🗺️"
              title="Route Builder"
              category="Puzzle · Pathfinding"
              description="Navigate a hex-grid terrain from checkpoint A to B using the fewest possible moves. Obstacles shift each round — every route is unique."
              delay={0.1}
            />
            <ActivityCard
              to="/activities/altitude-gauge"
              icon="⛰️"
              title="Altitude Gauge"
              category="Timing · Observation"
              description="An altitude marker oscillates across a vertical scale. Press when it enters the target elevation zone. Speed and precision determine your session score."
              delay={0.2}
            />
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: '40px' }}
          >
            <div style={{
              display: 'inline-block',
              padding: '10px 24px',
              border: '1px solid rgba(168,207,224,0.12)',
              borderRadius: '999px',
              fontSize: '0.78rem',
              color: 'rgba(168,207,224,0.45)',
            }}>
              All activities use session points only · No monetary value · No purchases
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── HOW SCORING WORKS ── */}
      <section className="section" style={{ background: 'rgba(255,255,255,0.02)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '60px', alignItems: 'center' }} className="scoring-grid">
            <div>
              <div className="label label-amber" style={{ marginBottom: '14px' }}>Session Points</div>
              <h2 style={{ color: '#fff', marginBottom: '16px' }}>How Scoring Works</h2>
              <div className="divider" />
              <p style={{ marginTop: '16px', marginBottom: '36px', maxWidth: '420px' }}>
                Session points are internal counters that track your performance during each visit. They reset when you close your browser and exist only as entertainment feedback.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <ScoringStep number={1} title="Open an activity" desc="No registration required. Session points appear automatically when an activity loads." />
                <ScoringStep number={2} title="Perform the navigation task" desc="Each activity measures a different skill — timing, spatial reasoning, or observation. Points reflect your accuracy." />
                <ScoringStep number={3} title="Points update in real time" desc="Your session tally grows as you interact. It's feedback, not currency — there is nothing to collect or redeem." />
                <ScoringStep number={4} title="Session ends, points clear" desc="Closing or refreshing the page clears your tally. There is no account, no leaderboard, and no persistent score." />
              </div>
            </div>

            {/* Point clarification box */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div style={{
                background: 'rgba(201,168,76,0.05)',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: '20px',
                padding: '32px',
              }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.72rem', color: 'rgba(201,168,76,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '20px' }}>
                  Session Points Are Not
                </div>
                {[
                  ['Currency or money', '✗'],
                  ['Redeemable for prizes', '✗'],
                  ['Purchasable with real money', '✗'],
                  ['Transferable to others', '✗'],
                  ['Connected to any reward', '✗'],
                ].map(([text, icon]) => (
                  <div key={text} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '11px 0', borderBottom: '1px solid rgba(201,168,76,0.1)',
                    fontSize: '0.9rem', color: 'rgba(168,207,224,0.7)',
                  }}>
                    <span>{text}</span>
                    <span style={{ color: 'rgba(201,168,76,0.5)', fontSize: '1rem' }}>{icon}</span>
                  </div>
                ))}
                <div style={{
                  marginTop: '20px', padding: '14px',
                  background: 'rgba(168,207,224,0.05)', borderRadius: '10px',
                  fontSize: '0.82rem', color: 'rgba(168,207,224,0.55)', lineHeight: 1.7,
                }}>
                  Points exist only as in-session entertainment feedback. They disappear when your session ends.
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <style>{`@media(min-width:900px){.scoring-grid{grid-template-columns:1fr 1fr !important;}}`}</style>
      </section>

      {/* ── WHO WE ARE ── */}
      <section className="section">
        <div className="container">
          <div style={{ maxWidth: '700px' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="label label-sky" style={{ marginBottom: '14px' }}>Who We Are</div>
              <h2 style={{ color: '#fff', marginBottom: '16px' }}>Built for Curious Adults</h2>
              <div className="divider" />
              <p style={{ marginTop: '18px', marginBottom: '18px' }}>
                Social North Hub was built for Canadian adults who enjoy browser-based activities that feel thoughtful rather than frantic. Navigation is our theme because it rewards patience, spatial awareness, and calm decision-making — qualities we wanted to bring into a digital pastime.
              </p>
              <p style={{ marginBottom: '28px' }}>
                Every activity here was designed from scratch. There are no third-party engines, no embedded external games, and no connection to any real-money platform. The compass, the hex grid, and the altitude gauge are original to this site.
              </p>
              <Link to="/about-hub" className="btn btn-outline">Read About the Hub</Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
